import { useState, useRef, useCallback } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { Gauge, Download, Upload, Activity, RotateCcw } from "lucide-react";

function useCounter() {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => v.toFixed(1));
  const set = (target) => animate(mv, target, { duration: 0.6, ease: "easeOut" });
  return { rounded, set, reset: () => mv.set(0) };
}

export function SpeedTest() {
  const [phase, setPhase] = useState("idle"); // idle | ping | download | upload | done
  const [ping, setPing] = useState(null);
  const [jitter, setJitter] = useState(null);
  const download = useCounter();
  const upload = useCounter();
  const [downFinal, setDownFinal] = useState(null);
  const [upFinal, setUpFinal] = useState(null);
  const running = useRef(false);

  const measurePing = useCallback(async () => {
    const samples = [];
    for (let i = 0; i < 6; i++) {
      const t0 = performance.now();
      await fetch(`${API}/speedtest/ping?t=${Date.now()}`, { cache: "no-store" });
      samples.push(performance.now() - t0);
    }
    samples.sort((a, b) => a - b);
    const best = samples.slice(0, 4);
    const avg = best.reduce((a, b) => a + b, 0) / best.length;
    const jit = Math.max(...best) - Math.min(...best);
    return { ping: avg, jitter: jit };
  }, []);

  const measureDownload = useCallback(async () => {
    const size = 25_000_000;
    const t0 = performance.now();
    const res = await fetch(`${API}/speedtest/download?bytes=${size}&t=${Date.now()}`, { cache: "no-store" });
    const reader = res.body.getReader();
    let received = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      received += value.length;
      const elapsed = (performance.now() - t0) / 1000;
      if (elapsed > 0) download.set(((received * 8) / elapsed) / 1e6);
    }
    const elapsed = (performance.now() - t0) / 1000;
    return ((received * 8) / elapsed) / 1e6;
  }, [download]);

  const measureUpload = useCallback(async () => {
    const size = 8_000_000;
    const payload = new Uint8Array(size);
    const t0 = performance.now();
    await fetch(`${API}/speedtest/upload?t=${Date.now()}`, {
      method: "POST",
      body: payload,
      headers: { "Content-Type": "application/octet-stream" },
    });
    const elapsed = (performance.now() - t0) / 1000;
    const mbps = ((size * 8) / elapsed) / 1e6;
    animate(0, mbps, { duration: 0.6, onUpdate: (v) => upload.set(v) });
    return mbps;
  }, [upload]);

  const start = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    setPing(null); setJitter(null); setDownFinal(null); setUpFinal(null);
    download.reset(); upload.reset();
    try {
      setPhase("ping");
      const p = await measurePing();
      setPing(Math.round(p.ping)); setJitter(Math.round(p.jitter));
      setPhase("download");
      const d = await measureDownload();
      setDownFinal(d.toFixed(1)); download.set(d);
      setPhase("upload");
      const u = await measureUpload();
      setUpFinal(u.toFixed(1));
      setPhase("done");
    } catch (e) {
      setPhase("idle");
    } finally {
      running.current = false;
    }
  }, [measurePing, measureDownload, measureUpload, download, upload]);

  const active = phase === "download" ? download.rounded : phase === "upload" ? upload.rounded : null;
  const busy = phase !== "idle" && phase !== "done";

  return (
    <div className="rounded-3xl bg-[#0d0d0f] text-white p-8 sm:p-12 grain overflow-hidden relative" data-testid="speedtest-widget">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-2 text-primary mb-8">
          <Gauge className="h-5 w-5" />
          <span className="text-xs font-bold uppercase tracking-[0.25em]">Medidor de velocidad</span>
        </div>

        <div className="grid place-items-center py-6">
          <div className="relative h-56 w-56 grid place-items-center">
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
              <circle
                cx="50" cy="50" r="44" fill="none" stroke="#FF5722" strokeWidth="6" strokeLinecap="round"
                strokeDasharray="276" strokeDashoffset={busy ? 60 : 276}
                className="transition-all duration-500"
                style={{ animation: busy ? "spin 1.5s linear infinite" : "none", transformOrigin: "center" }}
              />
            </svg>
            <div className="text-center">
              {active ? (
                <motion.span className="font-mono text-5xl font-bold tabular-nums">{active}</motion.span>
              ) : (
                <span className="font-mono text-5xl font-bold">
                  {phase === "done" ? downFinal : phase === "idle" ? "0.0" : "..."}
                </span>
              )}
              <div className="text-xs text-white/50 uppercase tracking-widest mt-1">Mbps</div>
              <div className="text-[10px] text-primary mt-2 h-4">
                {phase === "ping" && "Midiendo latencia..."}
                {phase === "download" && "Prueba de descarga"}
                {phase === "upload" && "Prueba de carga"}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-6">
          <Stat icon={<Activity className="h-4 w-4" />} label="Latencia" value={ping != null ? `${ping} ms` : "—"} />
          <Stat icon={<Download className="h-4 w-4" />} label="Descarga" value={downFinal != null ? `${downFinal}` : "—"} unit="Mbps" />
          <Stat icon={<Upload className="h-4 w-4" />} label="Carga" value={upFinal != null ? `${upFinal}` : "—"} unit="Mbps" />
        </div>
        {jitter != null && (
          <p className="text-center text-xs text-white/40 mt-4">Jitter: {jitter} ms</p>
        )}

        <div className="mt-8 flex justify-center">
          <Button
            onClick={start}
            disabled={busy}
            data-testid="speedtest-start-btn"
            className="rounded-full px-10 h-12 font-semibold gap-2 text-base"
          >
            {phase === "done" ? <><RotateCcw className="h-4 w-4" /> Repetir prueba</> : busy ? "Midiendo..." : "Iniciar prueba"}
          </Button>
        </div>
        <p className="text-center text-[11px] text-white/40 mt-4 max-w-md mx-auto">
          Los resultados dependen de tu red WiFi, dispositivo y servidores de destino. Para mayor precisión, conéctate por
          cable y cierra otras aplicaciones.
        </p>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function Stat({ icon, label, value, unit }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center">
      <div className="flex items-center justify-center gap-1.5 text-white/50 mb-2">{icon}<span className="text-[10px] uppercase tracking-wider">{label}</span></div>
      <div className="font-mono text-xl font-bold">{value}</div>
      {unit && <div className="text-[10px] text-white/40">{unit}</div>}
    </div>
  );
}
